import { atom, CallbackInterface, selector } from 'recoil';
import { FolderType, getProjectsInFolder, ProjectType } from '../../contexts/projectsContext';
import { loadPersistedValue, logOnSet, persistOnSet } from '../effects';
import { intersection, union, without, xor } from 'lodash';

// TODO: [S5-3741] Do we want to cache folders and projects for quick loading, or always load from DB?
// #region Folders and Projects
export const foldersState = atom<FolderType[]>({
  key: 'foldersState',
  default: [],
  effects: [loadPersistedValue, persistOnSet, logOnSet],
});

export const projectsState = atom<ProjectType[]>({
  key: 'projectsState',
  default: [],
  effects: [loadPersistedValue, persistOnSet, logOnSet],
});

export const projectIdsSelector = selector<Array<ProjectType['id']>>({
  key: 'projectIdsSelector',
  get: ({ get }) => get(projectsState).map(p => p.id),
});

export const getProjectIdsCallback =
  ({ snapshot: { getLoadable } }: CallbackInterface) =>
  (): string[] | undefined => {
    const { contents, state } = getLoadable(projectsState);
    return state === 'hasValue' ? contents.map(p => p.id) ?? undefined : undefined;
  };
// #endregion

// #region Selected Projects
export const selectedProjectsState = atom<Array<ProjectType['id']>>({
  key: 'selectedProjectsState',
  default: selector({
    key: 'selectedProjectsState.default',
    get: ({ get }) => get(projectsState).map(p => p.id),
    // cachePolicy_UNSTABLE: { eviction: 'most-recent' },
  }),
  effects: [loadPersistedValue, persistOnSet],
});

export const selectProjectCallback =
  ({ set }: CallbackInterface) =>
  (projectId: string, toggle?: boolean): void => {
    set(selectedProjectsState, prev => (toggle ? xor(prev, [projectId]) : union(prev, projectId)));
  };

export const selectAllProjectsCallback =
  ({ set, snapshot: { getLoadable } }: CallbackInterface) =>
  (toggleSet?: boolean): void => {
    set(selectedProjectsState, prev => {
      if (toggleSet && prev.length) {
        return [];
      }

      const { contents: projects, state: projectsLoadState } = getLoadable(projectsState);
      if (projectsLoadState !== 'hasValue') {
        return prev;
      }

      const projectIds = projects.map(p => p.id);
      return intersection(prev, projectIds).length !== projectIds.length ? projectIds : prev;
    });
  };

export const selectProjectsInFolderCallback =
  ({ set, snapshot: { getLoadable } }: CallbackInterface) =>
  (folderId: FolderType['id'], toggleSet?: boolean) => {
    const { contents: folders, state: foldersLoadState } = getLoadable(foldersState);
    if (foldersLoadState !== 'hasValue') {
      return;
    }
    const folder = folders.find(f => f.id === folderId);
    if (!folder) {
      return;
    }

    const projectIdsInFolder = getProjectsInFolder(folder).map(p => p.id);
    set(selectedProjectsState, prev => {
      const isSetSelected = intersection(prev, projectIdsInFolder).length === projectIdsInFolder.length;
      return !isSetSelected ? union(prev, projectIdsInFolder) : toggleSet ? without(prev, ...projectIdsInFolder) : prev;
    });
  };
// #endregion
